import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaChevronRight, FaCopy, FaDownload, FaExclamationTriangle, FaExternalLinkAlt, FaSearch, FaTrashAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface IntroducerOption {
    value: string
    label: string
}

const displayIntroducerLabel = (value: string, label?: string) => {
    const haystack = `${value} ${label || ''}`.toLowerCase()
    if (haystack.includes('fairclaim') || haystack.includes('fair claim')) {
        return 'Claim Delivery API'
    }
    return label || value
}

interface ClaimEntry {
    caseRef: string
    policyNumber: string
    lender: string
    lenderResolved: string | null
    loanValue: number | null
    monthlyRepayment: number | null
    lengthOfContract: string | number | null
    startDate: string | null
    quantum: number | null
    claimRejectionReason: string | null
    claimRejectionDetails: Record<string, unknown> | null
}

interface ClientBreakdownEntry {
    clientKey: string
    introducer: string
    introducerClientId: string | number | null
    name: string
    email: string
    dob: string | null
    ccjs: number
    voluntaryArrangements: number
    cfaPresent: boolean
    clientTotalValue: number
    claimCount: number
    eligibleClaimCount: number
    clientRejectionReason: string | null
    clientRejectionDetails: Record<string, unknown> | null
    claims: ClaimEntry[]
    inFundingBatch?: boolean
    fundingBatchGroups?: string[]
}

interface AnalysisResponse {
    success: boolean
    introducer: string
    params: {
        commissionPercentage: number
        statutoryInterestPercentage: number
        minimumClientValue: number
        calculationDate: string
    }
    counts: {
        totalClients: number
        clientsWithPolicyNumber: number
        clientsConsideredForFunding: number
        eligibleClients: number
        ineligibleClients: number
        eligibleUnfundedClients?: number
        eligibleUnfundedClaims?: number
        eligibleFundedClients?: number
        notInURClients?: number
        notInURClaims?: number
        batchAllocationsPartial?: boolean
        unresolvableErrorClients?: number
        headerCountsPartial?: boolean
    }
    summary: {
        totalBookValue?: number
        totalClaimsProcessed?: number
        totalClaimsExcluded?: number
        averageClientValue?: number
        eligibleUnfundedValue?: number
        notInURValue?: number
    }
    eligibleClients: ClientBreakdownEntry[]
    ineligibleClients: ClientBreakdownEntry[]
    message?: string
}

interface DeletePreviewResponse {
    success: boolean
    introducer: string
    clientCount: number
    totalRows: number
    byReason: Record<string, number>
    tableCounts: Record<string, number>
    skippedTables: string[]
    sample: Array<{
        introducerClientId: string | number
        name: string
        email: string
        reason: string | null
        claimCount: number
    }>
    message?: string
}

const CLIENT_REASON_LABELS: Record<string, string> = {
    clientsWithCcjOrVA: 'CCJ or Voluntary Arrangement',
    clientsExcludedMissingCfa: 'Missing CFA / signature date',
    clientsBelowMinimum: 'Below £1,700 minimum client value',
    clientsWithDuplicateEmail: 'Duplicate email in funder DB',
    clientsAllClaimsRejected: 'All claims rejected (no eligible value)'
}

const CLAIM_REASON_LABELS: Record<string, string> = {
    claimsExcludedDuplicatePolicyNumber: 'Duplicate policy number',
    claimsExcludedPre2007: 'Pre-2007 start date',
    claimsExcludedBelowMinLoan: 'Below £5,000 minimum loan',
    claimsExcludedUnapprovedLender: 'Unapproved lender',
    claimsExcludedNoLengthOfContract: 'Missing length of contract',
    claimsExcludedBelowMinContractLength: 'Contract length under 5 months',
    claimsExcludedBelowMinMonthlyRepayment: 'Monthly repayment < £50',
    claimsExcludedExcessiveMonthlyRepayment: 'Monthly repayment > £1,500'
}

const formatMoney = (v: number | null | undefined) => {
    if (typeof v !== 'number' || !isFinite(v)) return '—'
    return `£${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatNumber = (v: number | null | undefined) => {
    if (typeof v !== 'number' || !isFinite(v)) return '—'
    return v.toLocaleString('en-GB')
}

const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const labelClientReason = (reason: string | null) =>
    reason ? CLIENT_REASON_LABELS[reason] || reason : 'Unknown'

const labelClaimReason = (reason: string | null) => {
    if (!reason) return null
    if (CLAIM_REASON_LABELS[reason]) return CLAIM_REASON_LABELS[reason]
    if (reason.startsWith('claimsExcludedQuantum:')) return `Quantum exclusion: ${reason.split(':')[1]}`
    return reason
}

const claimMatchesQuery = (claim: ClaimEntry, q: string) => {
    if (!q) return true
    const haystack = [claim.caseRef, claim.policyNumber, claim.lender, claim.lenderResolved || '']
        .join(' ')
        .toLowerCase()
    return haystack.includes(q)
}

const clientMatchesQuery = (client: ClientBreakdownEntry, q: string) => {
    if (!q) return true
    const haystack = [client.name, client.email, String(client.introducerClientId || '')]
        .join(' ')
        .toLowerCase()
    if (haystack.includes(q)) return true
    return client.claims.some((claim) => claimMatchesQuery(claim, q))
}

// Build the "Evidence" cell for an ineligible client. Surfaces the concrete
// data points behind the failure: raw lender names for claims rejected as an
// unapproved lender, and loan values for claims rejected below the £5,000
// minimum (the two examples requested), plus a sensible note for the
// client-level reasons that aren't claim-derived.
const buildClientEvidence = (client: ClientBreakdownEntry): string => {
    const parts: string[] = []

    const unapprovedLenders = Array.from(
        new Set(
            client.claims
                .filter((c) => c.claimRejectionReason === 'claimsExcludedUnapprovedLender')
                .map((c) => (c.lender || '').trim())
                .filter(Boolean)
        )
    )
    if (unapprovedLenders.length) {
        parts.push(`Unapproved lender: ${unapprovedLenders.join(', ')}`)
    }

    const belowMinLoans = client.claims
        .filter((c) => c.claimRejectionReason === 'claimsExcludedBelowMinLoan')
        .map((c) => c.loanValue)
        .filter((v): v is number => typeof v === 'number' && isFinite(v))
    if (belowMinLoans.length) {
        parts.push(`Loan below £5,000: ${belowMinLoans.map((v) => formatMoney(v)).join(', ')}`)
    }

    switch (client.clientRejectionReason) {
        case 'clientsWithCcjOrVA': {
            const bits: string[] = []
            if (client.ccjs) bits.push(`${client.ccjs} CCJ${client.ccjs === 1 ? '' : 's'}`)
            if (client.voluntaryArrangements)
                bits.push(
                    `${client.voluntaryArrangements} voluntary arrangement${
                        client.voluntaryArrangements === 1 ? '' : 's'
                    }`
                )
            if (bits.length) parts.push(bits.join(', '))
            break
        }
        case 'clientsExcludedMissingCfa':
            parts.push('No CFA signature on file')
            break
        case 'clientsWithDuplicateEmail':
            parts.push('Email already exists in funder database')
            break
        default:
            break
    }

    return parts.join(' | ')
}

const escapeCsv = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return ''
    const str = String(value)
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`
    return str
}

// Plain numeric value (2dp) for spreadsheet-friendly money columns.
const csvMoney = (v: number | null | undefined): string =>
    typeof v === 'number' && isFinite(v) ? v.toFixed(2) : ''

export const AnalyseSchemaView = () => {
    const navigate = useNavigate()
    const [introducers, setIntroducers] = useState<IntroducerOption[]>([])
    const [introducersLoading, setIntroducersLoading] = useState(false)
    const [selectedIntroducer, setSelectedIntroducer] = useState<string>('')
    const [analysisLoading, setAnalysisLoading] = useState(false)
    const [analysisError, setAnalysisError] = useState<string | null>(null)
    const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null)
    const [activeTab, setActiveTab] = useState<'eligible' | 'ineligible' | 'eligibleUnfunded' | 'notInUR'>('ineligible')
    const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({})
    const [searchEligible, setSearchEligible] = useState<string>('')
    const [searchEligibleUnfunded, setSearchEligibleUnfunded] = useState<string>('')
    const [searchNotInUR, setSearchNotInUR] = useState<string>('')
    const [searchIneligible, setSearchIneligible] = useState<string>('')
    const [groupIneligible, setGroupIneligible] = useState<boolean>(true)
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})
    const [deletePreview, setDeletePreview] = useState<DeletePreviewResponse | null>(null)
    const [deletePreviewLoading, setDeletePreviewLoading] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleteConfirmText, setDeleteConfirmText] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        const fetchIntroducers = async () => {
            try {
                setIntroducersLoading(true)
                const response = await api({ url: '/introducers-list', data: {} })
                if (response.data?.success && Array.isArray(response.data.introducers)) {
                    setIntroducers(response.data.introducers)
                } else {
                    setIntroducers([])
                }
            } catch (err) {
                console.error('Failed to load introducers list:', err)
                setIntroducers([])
            } finally {
                setIntroducersLoading(false)
            }
        }
        fetchIntroducers()
    }, [])

    const handleAnalyse = useCallback(async () => {
        if (!selectedIntroducer) {
            Report.failure('Pick an introducer', 'Select an introducer before running the analysis.', 'OKAY')
            return
        }
        setAnalysisLoading(true)
        setAnalysisError(null)
        setAnalysis(null)
        setExpandedClients({})
        try {
            const response = await api({
                url: '/funding-schema-analysis',
                data: { introducer: selectedIntroducer }
            })
            const payload = response.data as AnalysisResponse
            if (!payload?.success) {
                throw new Error(payload?.message || 'Analysis failed')
            }
            setAnalysis(payload)
            setActiveTab(payload.counts.ineligibleClients > 0 ? 'ineligible' : 'eligible')
        } catch (err: any) {
            console.error('Schema analysis error:', err)
            const message = err?.response?.data?.message || err?.message || 'Analysis failed'
            setAnalysisError(message)
            Report.failure('Analysis failed', message, 'OKAY')
        } finally {
            setAnalysisLoading(false)
        }
    }, [selectedIntroducer])

    const toggleClient = useCallback((clientKey: string) => {
        setExpandedClients((prev) => ({ ...prev, [clientKey]: !prev[clientKey] }))
    }, [])

    const toggleGroup = useCallback((label: string) => {
        setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }))
    }, [])

    const expandAll = useCallback((clients: ClientBreakdownEntry[]) => {
        const next: Record<string, boolean> = {}
        for (const c of clients) next[c.clientKey] = true
        setExpandedClients((prev) => ({ ...prev, ...next }))
    }, [])

    const collapseAll = useCallback((clients: ClientBreakdownEntry[]) => {
        const next: Record<string, boolean> = {}
        for (const c of clients) next[c.clientKey] = false
        setExpandedClients((prev) => ({ ...prev, ...next }))
    }, [])

    const goToClientDetail = useCallback(
        (client: ClientBreakdownEntry) => {
            navigate(`/analyse-schema/client/${encodeURIComponent(client.clientKey)}`, {
                state: {
                    introducer: client.introducer,
                    introducerClientId: client.introducerClientId
                }
            })
        },
        [navigate]
    )

    const downloadIneligibleReport = useCallback(() => {
        if (!analysis || analysis.ineligibleClients.length === 0) {
            Report.failure('Nothing to download', 'There are no ineligible clients to report on.', 'OKAY')
            return
        }

        const headers = [
            'Client name',
            'Email',
            'Date of birth',
            'Case reference(s)',
            'Reason ineligible',
            'Evidence',
            'Client valuation (£)'
        ]

        const rows = analysis.ineligibleClients.map((client) =>
            [
                escapeCsv(client.name || ''),
                escapeCsv(client.email || ''),
                escapeCsv(formatDate(client.dob)),
                escapeCsv(
                    Array.from(new Set(client.claims.map((c) => (c.caseRef || '').trim()).filter(Boolean))).join(', ')
                ),
                escapeCsv(labelClientReason(client.clientRejectionReason)),
                escapeCsv(buildClientEvidence(client)),
                escapeCsv(csvMoney(client.clientTotalValue))
            ].join(',')
        )

        const csv = [headers.join(','), ...rows].join('\n')
        const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' })
        const blobUrl = URL.createObjectURL(blob)
        const stamp = new Date().toISOString().split('T')[0]
        const safeIntroducer = (analysis.introducer || 'introducer').replace(/[^a-z0-9]+/gi, '-')
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = `ineligible-clients-${safeIntroducer}-${stamp}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobUrl)
    }, [analysis])

    // Shared CSV blob download used by the report buttons below.
    const triggerCsvDownload = useCallback((csv: string, filename: string) => {
        const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' })
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobUrl)
    }, [])

    // ------------------------------------------------------------------
    // Delete unfundable clients: fetch a server-side preview of exactly what
    // would be removed, show it in a modal, and require the user to type
    // "delete" before running the destructive call. The server recomputes the
    // unfundable set at delete time, so the preview can never delete more
    // than what is unfundable at that moment.
    // ------------------------------------------------------------------
    const openDeletePreview = useCallback(async () => {
        if (!analysis) return
        setDeletePreviewLoading(true)
        try {
            const response = await api({
                url: '/funding-schema-delete-unfundable-preview',
                data: { introducer: analysis.introducer }
            })
            const payload = response.data as DeletePreviewResponse
            if (!payload?.success) throw new Error(payload?.message || 'Preview failed')
            setDeletePreview(payload)
            setDeleteConfirmText('')
            setShowDeleteModal(true)
        } catch (err: any) {
            console.error('Delete preview error:', err)
            const message = err?.response?.data?.message || err?.message || 'Preview failed'
            Report.failure('Preview failed', message, 'OKAY')
        } finally {
            setDeletePreviewLoading(false)
        }
    }, [analysis])

    const closeDeleteModal = useCallback(() => {
        if (isDeleting) return
        setShowDeleteModal(false)
        setDeletePreview(null)
        setDeleteConfirmText('')
    }, [isDeleting])

    const handleDeleteUnfundable = useCallback(async () => {
        if (!analysis || deleteConfirmText.toLowerCase() !== 'delete') return
        setIsDeleting(true)
        try {
            const response = await api({
                url: '/funding-schema-delete-unfundable',
                data: { introducer: analysis.introducer, confirm: 'DELETE' }
            })
            const payload = response.data
            if (!payload?.success) throw new Error(payload?.message || 'Delete failed')
            setShowDeleteModal(false)
            setDeletePreview(null)
            setDeleteConfirmText('')
            const deleted = payload.deleted || {}
            Report.success(
                'Unfundable clients deleted',
                `${formatNumber(payload.clientCount)} clients removed — ` +
                    `${formatNumber(deleted.leads)} leads, ${formatNumber(deleted.lead_states)} lead states, ` +
                    `${formatNumber(deleted.signature_links)} signatures, ${formatNumber(deleted.documents)} documents ` +
                    `(${formatNumber(payload.totalDeleted)} rows in total).`,
                'OKAY',
                () => handleAnalyse()
            )
        } catch (err: any) {
            console.error('Delete unfundable error:', err)
            const message = err?.response?.data?.message || err?.message || 'Delete failed'
            Report.failure('Delete failed', message, 'OKAY')
        } finally {
            setIsDeleting(false)
        }
    }, [analysis, deleteConfirmText, handleAnalyse])

    // One row per claim, with the client's details repeated on each of their
    // rows, so the CSV is spreadsheet-friendly (filter/pivot by client email).
    // `scope` narrows the report to eligible clients not yet in a funding
    // batch ('unfunded') or covers every eligible client ('all').
    const downloadEligibleReport = useCallback(
        (scope: 'all' | 'unfunded') => {
            if (!analysis) return
            const clients =
                scope === 'unfunded'
                    ? analysis.eligibleClients.filter((c) => !c.inFundingBatch)
                    : analysis.eligibleClients

            if (clients.length === 0) {
                Report.failure(
                    'Nothing to download',
                    scope === 'unfunded'
                        ? 'There are no eligible unfunded clients to report on.'
                        : 'There are no eligible clients to report on.',
                    'OKAY'
                )
                return
            }

            const headers = [
                'Client name',
                'Email',
                'Date of birth',
                'Client valuation (£)',
                'Eligible claims',
                'Funding status',
                'Case reference',
                'Policy number',
                'Lender',
                'Lender (resolved)',
                'Loan value (£)',
                'Monthly repayment (£)',
                'Term (months)',
                'Start date',
                'Claim quantum (£)',
                'Claim status'
            ]

            const rows: string[] = []
            for (const client of clients) {
                const clientCells = [
                    escapeCsv(client.name || ''),
                    escapeCsv(client.email || ''),
                    escapeCsv(formatDate(client.dob)),
                    escapeCsv(csvMoney(client.clientTotalValue)),
                    escapeCsv(client.eligibleClaimCount),
                    escapeCsv(client.inFundingBatch ? 'In batch' : 'Unfunded')
                ]
                if (client.claims.length === 0) {
                    rows.push([...clientCells, '', '', '', '', '', '', '', '', '', ''].join(','))
                    continue
                }
                for (const claim of client.claims) {
                    rows.push(
                        [
                            ...clientCells,
                            escapeCsv(claim.caseRef || ''),
                            escapeCsv(claim.policyNumber || ''),
                            escapeCsv(claim.lender || ''),
                            escapeCsv(claim.lenderResolved || ''),
                            escapeCsv(csvMoney(claim.loanValue)),
                            escapeCsv(csvMoney(claim.monthlyRepayment)),
                            escapeCsv(claim.lengthOfContract ?? ''),
                            escapeCsv(formatDate(claim.startDate)),
                            escapeCsv(csvMoney(claim.quantum)),
                            escapeCsv(
                                claim.claimRejectionReason
                                    ? `Excluded — ${labelClaimReason(claim.claimRejectionReason)}`
                                    : 'Eligible'
                            )
                        ].join(',')
                    )
                }
            }

            const csv = [headers.join(','), ...rows].join('\n')
            const stamp = new Date().toISOString().split('T')[0]
            const safeIntroducer = (analysis.introducer || 'introducer').replace(/[^a-z0-9]+/gi, '-')
            const prefix =
                scope === 'unfunded' ? 'eligible-unfunded-clients-claims' : 'eligible-clients-claims'
            triggerCsvDownload(csv, `${prefix}-${safeIntroducer}-${stamp}.csv`)
        },
        [analysis, triggerCsvDownload]
    )

    // Not in UR: every analysed client — eligible or ineligible — with no
    // active funding-batch reference (per the funder_references cross-check
    // done server-side). These clients and claims definitely did not get
    // funded. Eligible clients first, then ineligible.
    const notInUR = useMemo(() => {
        if (!analysis) return [] as Array<ClientBreakdownEntry & { isEligible: boolean }>
        const eligible = analysis.eligibleClients
            .filter((c) => !c.inFundingBatch)
            .map((c) => ({ ...c, isEligible: true }))
        const ineligible = analysis.ineligibleClients
            .filter((c) => !c.inFundingBatch)
            .map((c) => ({ ...c, isEligible: false }))
        return [...eligible, ...ineligible]
    }, [analysis])

    const filteredNotInUR = useMemo(() => {
        const q = searchNotInUR.trim().toLowerCase()
        if (!q) return notInUR
        return notInUR.filter((c) => clientMatchesQuery(c, q))
    }, [notInUR, searchNotInUR])

    const notInURStats = useMemo(() => {
        const clients = notInUR.length
        const claims = notInUR.reduce((sum, c) => sum + (c.claims?.length || 0), 0)
        const value = notInUR.reduce((sum, c) => sum + (c.clientTotalValue || 0), 0)
        return { clients, claims, value }
    }, [notInUR])

    // One row per claim for every client not currently in a UR (funding
    // batch), with eligibility status and reason so the reader can see why
    // each unfunded client did or didn't qualify.
    const downloadNotInURReport = useCallback(() => {
        if (!analysis) return
        if (notInUR.length === 0) {
            Report.failure(
                'Nothing to download',
                'Every analysed client for this introducer is already in a UR.',
                'OKAY'
            )
            return
        }

        const headers = [
            'Client name',
            'Email',
            'Date of birth',
            'Eligibility',
            'Reason ineligible',
            'Client valuation (£)',
            'Total claims',
            'Case reference',
            'Policy number',
            'Lender',
            'Lender (resolved)',
            'Loan value (£)',
            'Monthly repayment (£)',
            'Term (months)',
            'Start date',
            'Claim quantum (£)',
            'Claim status'
        ]

        const rows: string[] = []
        for (const client of notInUR) {
            const clientCells = [
                escapeCsv(client.name || ''),
                escapeCsv(client.email || ''),
                escapeCsv(formatDate(client.dob)),
                escapeCsv(client.isEligible ? 'Eligible' : 'Ineligible'),
                escapeCsv(client.isEligible ? '' : labelClientReason(client.clientRejectionReason)),
                escapeCsv(csvMoney(client.clientTotalValue)),
                escapeCsv(client.claims?.length || 0)
            ]
            if (!client.claims || client.claims.length === 0) {
                rows.push([...clientCells, '', '', '', '', '', '', '', '', '', ''].join(','))
                continue
            }
            for (const claim of client.claims) {
                rows.push(
                    [
                        ...clientCells,
                        escapeCsv(claim.caseRef || ''),
                        escapeCsv(claim.policyNumber || ''),
                        escapeCsv(claim.lender || ''),
                        escapeCsv(claim.lenderResolved || ''),
                        escapeCsv(csvMoney(claim.loanValue)),
                        escapeCsv(csvMoney(claim.monthlyRepayment)),
                        escapeCsv(claim.lengthOfContract ?? ''),
                        escapeCsv(formatDate(claim.startDate)),
                        escapeCsv(csvMoney(claim.quantum)),
                        escapeCsv(
                            claim.claimRejectionReason
                                ? `Excluded — ${labelClaimReason(claim.claimRejectionReason)}`
                                : 'Eligible'
                        )
                    ].join(',')
                )
            }
        }

        const csv = [headers.join(','), ...rows].join('\n')
        const stamp = new Date().toISOString().split('T')[0]
        const safeIntroducer = (analysis.introducer || 'introducer').replace(/[^a-z0-9]+/gi, '-')
        triggerCsvDownload(csv, `not-in-ur-clients-claims-${safeIntroducer}-${stamp}.csv`)
    }, [analysis, notInUR, triggerCsvDownload])

    const filteredEligible = useMemo(() => {
        if (!analysis) return [] as ClientBreakdownEntry[]
        const q = searchEligible.trim().toLowerCase()
        if (!q) return analysis.eligibleClients
        return analysis.eligibleClients.filter((c) => clientMatchesQuery(c, q))
    }, [analysis, searchEligible])

    // Eligible clients that are not yet sitting in a funding batch
    // (per the funder_references cross-check done server-side).
    const eligibleUnfunded = useMemo(() => {
        if (!analysis) return [] as ClientBreakdownEntry[]
        return analysis.eligibleClients.filter((c) => !c.inFundingBatch)
    }, [analysis])

    const filteredEligibleUnfunded = useMemo(() => {
        const q = searchEligibleUnfunded.trim().toLowerCase()
        if (!q) return eligibleUnfunded
        return eligibleUnfunded.filter((c) => clientMatchesQuery(c, q))
    }, [eligibleUnfunded, searchEligibleUnfunded])

    const eligibleUnfundedStats = useMemo(() => {
        const clients = eligibleUnfunded.length
        const claims = eligibleUnfunded.reduce((sum, c) => sum + (c.eligibleClaimCount || 0), 0)
        const value = eligibleUnfunded.reduce((sum, c) => sum + (c.clientTotalValue || 0), 0)
        return { clients, claims, value }
    }, [eligibleUnfunded])

    const filteredIneligible = useMemo(() => {
        if (!analysis) return [] as ClientBreakdownEntry[]
        const q = searchIneligible.trim().toLowerCase()
        if (!q) return analysis.ineligibleClients
        return analysis.ineligibleClients.filter((c) => clientMatchesQuery(c, q))
    }, [analysis, searchIneligible])

    // Group the (filtered) ineligible clients by their client-level rejection
    // reason so the list can be browsed by category (e.g. "Below £1,700
    // minimum client value", "CCJ or Voluntary Arrangement", …). Groups are
    // ordered largest-first; the clients within each keep the parent sort.
    const ineligibleGroups = useMemo(() => {
        const map = new Map<string, ClientBreakdownEntry[]>()
        for (const client of filteredIneligible) {
            const label = labelClientReason(client.clientRejectionReason)
            const bucket = map.get(label)
            if (bucket) bucket.push(client)
            else map.set(label, [client])
        }
        return Array.from(map.entries())
            .map(([label, clients]) => ({ label, clients }))
            .sort((a, b) => b.clients.length - a.clients.length || a.label.localeCompare(b.label))
    }, [filteredIneligible])

    // Aggregate every claim that was rejected for "unapproved lender" by its raw
    // lender string. The result feeds the "Unapproved lenders" section so you
    // can copy the names straight into the lender alias mapping.
    const unapprovedLenders = useMemo(() => {
        if (!analysis) return [] as Array<{ name: string; count: number }>
        const counts = new Map<string, number>()
        for (const client of analysis.ineligibleClients) {
            for (const claim of client.claims) {
                if (claim.claimRejectionReason !== 'claimsExcludedUnapprovedLender') continue
                const raw = (claim.lender || '').trim()
                const key = raw || '(no lender on claim)'
                counts.set(key, (counts.get(key) || 0) + 1)
            }
        }
        return Array.from(counts.entries())
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    }, [analysis])

    const totalUnapprovedClaims = useMemo(
        () => unapprovedLenders.reduce((sum, l) => sum + l.count, 0),
        [unapprovedLenders]
    )

    const [lendersOpen, setLendersOpen] = useState<boolean>(true)
    const [lendersCopied, setLendersCopied] = useState<null | 'list' | 'csv'>(null)

    const copyLendersList = useCallback(async () => {
        const text = unapprovedLenders.map((l) => l.name).join('\n')
        try {
            await navigator.clipboard.writeText(text)
            setLendersCopied('list')
            setTimeout(() => setLendersCopied(null), 1500)
        } catch (err) {
            console.error('Failed to copy lender list:', err)
        }
    }, [unapprovedLenders])

    const copyLendersCsv = useCallback(async () => {
        const header = 'lender,claim_count'
        const rows = unapprovedLenders.map((l) => `"${l.name.replace(/"/g, '""')}",${l.count}`)
        const text = [header, ...rows].join('\n')
        try {
            await navigator.clipboard.writeText(text)
            setLendersCopied('csv')
            setTimeout(() => setLendersCopied(null), 1500)
        } catch (err) {
            console.error('Failed to copy lender CSV:', err)
        }
    }, [unapprovedLenders])

    const renderClaimsTable = (
        claims: ClaimEntry[],
        showReason: boolean,
        eligibleChipLabel: string = 'Eligible (client failed)'
    ) => (
        <div className={styles.claimsTableWrapper}>
            <table className={styles.claimsTable}>
                <thead>
                    <tr>
                        <th className={styles.colRefs}>Reference</th>
                        <th className={styles.colLender}>Lender</th>
                        <th className={styles.colLoan}>Loan / Monthly</th>
                        <th className={styles.colTerm}>Term / Start</th>
                        <th className={styles.colQuantum}>Quantum</th>
                        {showReason && <th className={styles.colStatus}>Status</th>}
                    </tr>
                </thead>
                <tbody>
                    {claims.map((claim, idx) => {
                        const reasonLabel = labelClaimReason(claim.claimRejectionReason)
                        return (
                            <tr key={`${claim.caseRef || idx}-${claim.policyNumber || idx}`}>
                                <td className={styles.colRefs}>
                                    <div className={styles.stackedCell}>
                                        <span className={styles.stackedPrimary}>
                                            {claim.caseRef || '—'}
                                        </span>
                                        <span className={styles.stackedSecondary}>
                                            <span className={styles.stackedLabel}>Policy</span>{' '}
                                            {claim.policyNumber || '—'}
                                        </span>
                                    </div>
                                </td>
                                <td className={styles.colLender}>
                                    <div className={styles.stackedCell}>
                                        <span
                                            className={styles.stackedPrimary}
                                            title={claim.lender || ''}
                                        >
                                            {claim.lender || '—'}
                                        </span>
                                        {claim.lenderResolved &&
                                            claim.lenderResolved !== claim.lender && (
                                                <span
                                                    className={styles.stackedSecondary}
                                                    title={claim.lenderResolved}
                                                >
                                                    <span className={styles.stackedLabel}>→</span>{' '}
                                                    {claim.lenderResolved}
                                                </span>
                                            )}
                                    </div>
                                </td>
                                <td className={styles.colLoan}>
                                    <div className={styles.stackedCell}>
                                        <span className={styles.stackedPrimary}>
                                            {formatMoney(claim.loanValue)}
                                        </span>
                                        <span className={styles.stackedSecondary}>
                                            {formatMoney(claim.monthlyRepayment)}
                                            <span className={styles.stackedLabel}> / mo</span>
                                        </span>
                                    </div>
                                </td>
                                <td className={styles.colTerm}>
                                    <div className={styles.stackedCell}>
                                        <span className={styles.stackedPrimary}>
                                            {claim.lengthOfContract != null &&
                                            claim.lengthOfContract !== ''
                                                ? `${claim.lengthOfContract} mo`
                                                : '—'}
                                        </span>
                                        <span className={styles.stackedSecondary}>
                                            {formatDate(claim.startDate)}
                                        </span>
                                    </div>
                                </td>
                                <td className={`${styles.colQuantum} ${styles.numericCell}`}>
                                    {formatMoney(claim.quantum)}
                                </td>
                                {showReason && (
                                    <td className={styles.colStatus}>
                                        {reasonLabel ? (
                                            <span className={`${styles.chip} ${styles.chipClaim}`}>
                                                {reasonLabel}
                                            </span>
                                        ) : (
                                            <span className={`${styles.chip} ${styles.chipOk}`}>
                                                {eligibleChipLabel}
                                            </span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )

    const renderEligibleAccordion = (clients: ClientBreakdownEntry[], emptyMessage: string, showBatchStatus: boolean) => {
        if (clients.length === 0) {
            return <div className={styles.emptyState}>{emptyMessage}</div>
        }
        return (
            <div className={styles.accordion}>
                {clients.map((client) => {
                    const isOpen = !!expandedClients[client.clientKey]
                    return (
                        <div
                            key={client.clientKey}
                            className={`${styles.accordionRow} ${isOpen ? styles.accordionRowOpen : ''}`}
                        >
                            <div className={styles.accordionHeaderRow}>
                                <button
                                    type="button"
                                    className={styles.accordionHeader}
                                    onClick={() => toggleClient(client.clientKey)}
                                    aria-expanded={isOpen}
                                >
                                    <FaChevronRight
                                        className={`${styles.accordionChevron} ${
                                            isOpen ? styles.accordionChevronOpen : ''
                                        }`}
                                    />
                                    <span className={styles.clientName}>{client.name || '—'}</span>
                                    <span className={styles.clientEmail}>{client.email || '—'}</span>
                                    <span className={styles.clientMeta}>
                                        {formatNumber(client.eligibleClaimCount)}{' '}
                                        {client.eligibleClaimCount === 1 ? 'claim' : 'claims'}
                                        {showBatchStatus && (
                                            <span
                                                className={`${styles.chip} ${styles.metaChip} ${
                                                    client.inFundingBatch ? styles.chipOk : styles.chipUnfunded
                                                }`}
                                                title={
                                                    client.inFundingBatch
                                                        ? `Already in funding batch${
                                                              client.fundingBatchGroups?.length
                                                                  ? `: ${client.fundingBatchGroups.join(', ')}`
                                                                  : ''
                                                          }`
                                                        : 'Fundable but not yet added to a funding batch'
                                                }
                                            >
                                                {client.inFundingBatch ? 'In batch' : 'Unfunded'}
                                            </span>
                                        )}
                                    </span>
                                    <span className={styles.clientValue}>
                                        {formatMoney(client.clientTotalValue)}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className={styles.viewButton}
                                    onClick={() => goToClientDetail(client)}
                                    title="Open client deep-dive"
                                >
                                    View <FaExternalLinkAlt className={styles.viewButtonIcon} />
                                </button>
                            </div>
                            {isOpen && renderClaimsTable(client.claims, false)}
                        </div>
                    )
                })}
            </div>
        )
    }

    // Rows for the "Not in UR" tab: a mixed list of eligible and ineligible
    // clients, so each row carries an eligibility chip (green "Eligible" or
    // red rejection reason) and the expanded claims table shows per-claim
    // status.
    const renderNotInURAccordion = (clients: Array<ClientBreakdownEntry & { isEligible: boolean }>) => {
        if (clients.length === 0) {
            return (
                <div className={styles.emptyState}>
                    {notInUR.length > 0
                        ? 'No clients match your search.'
                        : 'Every analysed client for this introducer is already in a UR.'}
                </div>
            )
        }
        return (
            <div className={styles.accordion}>
                {clients.map((client) => {
                    const isOpen = !!expandedClients[client.clientKey]
                    return (
                        <div
                            key={client.clientKey}
                            className={`${styles.accordionRow} ${isOpen ? styles.accordionRowOpen : ''}`}
                        >
                            <div className={styles.accordionHeaderRow}>
                                <button
                                    type="button"
                                    className={styles.accordionHeader}
                                    onClick={() => toggleClient(client.clientKey)}
                                    aria-expanded={isOpen}
                                >
                                    <FaChevronRight
                                        className={`${styles.accordionChevron} ${
                                            isOpen ? styles.accordionChevronOpen : ''
                                        }`}
                                    />
                                    <span className={styles.clientName}>{client.name || '—'}</span>
                                    <span className={styles.clientEmail}>{client.email || '—'}</span>
                                    <span className={styles.clientMeta}>
                                        {formatNumber(client.claims?.length || 0)}{' '}
                                        {(client.claims?.length || 0) === 1 ? 'claim' : 'claims'}
                                        <span
                                            className={`${styles.chip} ${styles.metaChip} ${
                                                client.isEligible ? styles.chipOk : styles.chipClient
                                            }`}
                                            title={
                                                client.isEligible
                                                    ? 'Eligible for funding but not in any UR'
                                                    : labelClientReason(client.clientRejectionReason)
                                            }
                                        >
                                            {client.isEligible
                                                ? 'Eligible'
                                                : labelClientReason(client.clientRejectionReason)}
                                        </span>
                                    </span>
                                    <span className={styles.clientValue}>
                                        {formatMoney(client.clientTotalValue)}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className={styles.viewButton}
                                    onClick={() => goToClientDetail(client)}
                                    title="Open client deep-dive"
                                >
                                    View <FaExternalLinkAlt className={styles.viewButtonIcon} />
                                </button>
                            </div>
                            {isOpen &&
                                renderClaimsTable(
                                    client.claims,
                                    true,
                                    client.isEligible ? 'Eligible' : 'Eligible (client failed)'
                                )}
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderIneligibleClientRow = (client: ClientBreakdownEntry) => {
        const isOpen = !!expandedClients[client.clientKey]
        const reasonLabel = labelClientReason(client.clientRejectionReason)
        return (
            <div
                key={client.clientKey}
                className={`${styles.accordionRow} ${isOpen ? styles.accordionRowOpen : ''}`}
            >
                <div className={styles.accordionHeaderRow}>
                    <button
                        type="button"
                        className={styles.accordionHeader}
                        onClick={() => toggleClient(client.clientKey)}
                        aria-expanded={isOpen}
                    >
                        <FaChevronRight
                            className={`${styles.accordionChevron} ${
                                isOpen ? styles.accordionChevronOpen : ''
                            }`}
                        />
                        <span className={styles.clientName}>{client.name || '—'}</span>
                        <span className={styles.clientEmail}>{client.email || '—'}</span>
                        <span className={styles.clientMeta}>
                            {formatNumber(client.claimCount)}{' '}
                            {client.claimCount === 1 ? 'claim' : 'claims'}
                        </span>
                        <span className={`${styles.chip} ${styles.chipClient}`}>{reasonLabel}</span>
                    </button>
                    <button
                        type="button"
                        className={styles.viewButton}
                        onClick={() => goToClientDetail(client)}
                        title="Open client deep-dive"
                    >
                        View <FaExternalLinkAlt className={styles.viewButtonIcon} />
                    </button>
                </div>
                {isOpen && (
                    <div className={styles.accordionBody}>
                        {client.clientRejectionDetails && (
                            <div className={styles.detailGrid}>
                                {Object.entries(client.clientRejectionDetails).map(([k, v]) => (
                                    <div key={k} className={styles.detailItem}>
                                        <span className={styles.detailLabel}>{k}</span>
                                        <span className={styles.detailValue}>
                                            {typeof v === 'number' && /value|required|monthly/i.test(k)
                                                ? formatMoney(v as number)
                                                : String(v ?? '—')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {renderClaimsTable(client.claims, true)}
                    </div>
                )}
            </div>
        )
    }

    const renderIneligibleAccordion = (clients: ClientBreakdownEntry[]) => {
        if (clients.length === 0) {
            return (
                <div className={styles.emptyState}>
                    {analysis && analysis.ineligibleClients.length > 0
                        ? 'No clients match your search.'
                        : 'No ineligible clients for this introducer.'}
                </div>
            )
        }

        // Flat (ungrouped) view.
        if (!groupIneligible) {
            return <div className={styles.accordion}>{clients.map(renderIneligibleClientRow)}</div>
        }

        // Grouped-by-reason view.
        return (
            <div className={styles.groupedAccordion}>
                {ineligibleGroups.map((group) => {
                    const isGroupOpen = !collapsedGroups[group.label]
                    return (
                        <div key={group.label} className={styles.reasonGroup}>
                            <button
                                type="button"
                                className={styles.reasonGroupHeader}
                                onClick={() => toggleGroup(group.label)}
                                aria-expanded={isGroupOpen}
                            >
                                <FaChevronRight
                                    className={`${styles.reasonGroupChevron} ${
                                        isGroupOpen ? styles.reasonGroupChevronOpen : ''
                                    }`}
                                />
                                <span className={styles.reasonGroupTitle}>{group.label}</span>
                                <span className={styles.reasonGroupCount}>
                                    {formatNumber(group.clients.length)}
                                </span>
                            </button>
                            {isGroupOpen && (
                                <div className={styles.accordion}>
                                    {group.clients.map(renderIneligibleClientRow)}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <div className={styles.analyseSchemaContainer}>
            <div className={styles.formCard}>
                <div className={styles.formHeader}>
                    <h3>Analyse a single introducer</h3>
                    <p className={styles.description}>
                        Pick an introducer and we&rsquo;ll run every signed client and their claims through the
                        funding eligibility rules. Nothing is written to the database — this is a read-only
                        preview of who would (and would not) make it into a funding batch right now.
                    </p>
                </div>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label htmlFor="introducer">Introducer</label>
                        <select
                            id="introducer"
                            value={selectedIntroducer}
                            onChange={(e) => setSelectedIntroducer(e.target.value)}
                            disabled={introducersLoading || analysisLoading}
                        >
                            <option value="">
                                {introducersLoading ? 'Loading introducers…' : 'Select an introducer'}
                            </option>
                            {introducers.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {displayIntroducerLabel(option.value, option.label)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroupAction}>
                        <button
                            type="button"
                            className={styles.analyseButton}
                            onClick={handleAnalyse}
                            disabled={!selectedIntroducer || analysisLoading}
                        >
                            {analysisLoading ? 'Analysing…' : 'Analyse'}
                        </button>
                    </div>
                </div>
                <div className={styles.paramsCard}>
                    <h4>Rules applied</h4>
                    <ul>
                        <li>Commission: 10%</li>
                        <li>Statutory interest: 8%</li>
                        <li>Minimum client value: £1,700</li>
                        <li>Calculation date: today + 1 year + 2 months</li>
                    </ul>
                </div>
            </div>

            {analysisError && <div className={styles.errorBanner}>{analysisError}</div>}

            {analysis && (
                <div className={styles.resultsCard}>
                    {/*
                     * Summary funnel — each tile narrows the previous one:
                     *   totalClients
                     *     -> clientsWithPolicyNumber  (≥1 claim has a policy_number)
                     *     -> clientsConsideredForFunding  (live, un-allocated claim)
                     *     -> eligibleClients + ineligibleClients
                     * The "Total book value" tile sits at the end because it's
                     * orthogonal to the funnel.
                     */}
                    <div className={styles.summaryGrid}>
                        <div
                            className={styles.summaryItem}
                            title="Every row in the introducer's clients table"
                        >
                            <span className={styles.summaryLabel}>Total clients</span>
                            <span className={styles.summaryValue}>
                                {formatNumber(analysis.counts.totalClients)}
                            </span>
                            <span className={styles.summaryCaption}>All in introducer DB</span>
                        </div>
                        <div
                            className={styles.summaryItem}
                            title="Distinct clients with at least one claim that has a policy number"
                        >
                            <span className={styles.summaryLabel}>With policy number</span>
                            <span className={styles.summaryValue}>
                                {formatNumber(analysis.counts.clientsWithPolicyNumber)}
                            </span>
                            <span className={styles.summaryCaption}>≥1 claim has a policy</span>
                        </div>
                        <div
                            className={styles.summaryItem}
                            title="Clients passed into the eligibility engine — at least one live, un-allocated claim with a policy number"
                        >
                            <span className={styles.summaryLabel}>Considered for funding</span>
                            <span className={styles.summaryValue}>
                                {formatNumber(analysis.counts.clientsConsideredForFunding)}
                            </span>
                            <span className={styles.summaryCaption}>Live & un-allocated</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Eligible</span>
                            <span className={`${styles.summaryValue} ${styles.summaryValueOk}`}>
                                {formatNumber(analysis.counts.eligibleClients)}
                            </span>
                        </div>
                        <div
                            className={styles.summaryItem}
                            title="Eligible clients not yet added to any funding batch"
                        >
                            <span className={styles.summaryLabel}>Eligible unfunded</span>
                            <span className={`${styles.summaryValue} ${styles.summaryValueUnfunded}`}>
                                {formatNumber(eligibleUnfundedStats.clients)}
                            </span>
                            <span className={styles.summaryCaption}>
                                {formatNumber(eligibleUnfundedStats.claims)}{' '}
                                {eligibleUnfundedStats.claims === 1 ? 'claim' : 'claims'} not in a batch
                            </span>
                        </div>
                        <div
                            className={styles.summaryItem}
                            title="Every analysed client — eligible or ineligible — with no active funding batch reference. These definitely did not get funded."
                        >
                            <span className={styles.summaryLabel}>Not in UR</span>
                            <span className={`${styles.summaryValue} ${styles.summaryValueUnfunded}`}>
                                {formatNumber(notInURStats.clients)}
                            </span>
                            <span className={styles.summaryCaption}>
                                {formatNumber(notInURStats.claims)}{' '}
                                {notInURStats.claims === 1 ? 'claim' : 'claims'} never funded
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Ineligible for funding</span>
                            <span className={`${styles.summaryValue} ${styles.summaryValueWarn}`}>
                                {formatNumber(analysis.counts.ineligibleClients)}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Total book value</span>
                            <span className={styles.summaryValue}>
                                {formatMoney(analysis.summary?.totalBookValue ?? 0)}
                            </span>
                        </div>
                    </div>
                    {analysis.counts.headerCountsPartial && (
                        <div className={styles.errorBanner}>
                            One or more introducer DBs failed to return header counts — the
                            &ldquo;Total clients&rdquo; and &ldquo;With policy number&rdquo; tiles
                            may be under-reported.
                        </div>
                    )}
                    {analysis.counts.batchAllocationsPartial && (
                        <div className={styles.errorBanner}>
                            The funder database could not be checked for existing batch
                            allocations — the &ldquo;Eligible unfunded&rdquo; figures may
                            over-count clients that are already in a funding batch.
                        </div>
                    )}

                    {!!analysis.counts.unresolvableErrorClients &&
                        analysis.counts.unresolvableErrorClients > 0 && (
                            <div className={styles.caveatBanner}>
                                <strong>
                                    {formatNumber(analysis.counts.unresolvableErrorClients)}
                                </strong>{' '}
                                client
                                {analysis.counts.unresolvableErrorClients === 1 ? ' is' : 's are'}{' '}
                                flagged with an <strong>unresolvable error</strong> and are excluded
                                from this analysis up-front — they never reach the eligibility engine
                                and will never be considered for a funding batch, so they are not
                                counted in &ldquo;Considered for funding&rdquo;, &ldquo;Eligible&rdquo;
                                or &ldquo;Ineligible for funding&rdquo; below.
                            </div>
                        )}

                    {unapprovedLenders.length > 0 && (
                        <div
                            className={`${styles.lendersCard} ${
                                lendersOpen ? styles.lendersCardOpen : ''
                            }`}
                        >
                            <div className={styles.lendersHeader}>
                                <button
                                    type="button"
                                    className={styles.lendersToggle}
                                    onClick={() => setLendersOpen((s) => !s)}
                                    aria-expanded={lendersOpen}
                                >
                                    <FaChevronRight
                                        className={`${styles.lendersChevron} ${
                                            lendersOpen ? styles.lendersChevronOpen : ''
                                        }`}
                                    />
                                    <div className={styles.lendersTitleStack}>
                                        <span className={styles.lendersTitle}>
                                            Unapproved lenders ({unapprovedLenders.length})
                                        </span>
                                        <span className={styles.lendersSubtitle}>
                                            {formatNumber(totalUnapprovedClaims)} claim
                                            {totalUnapprovedClaims === 1 ? '' : 's'} rejected — add
                                            these names to your lender alias mapping
                                        </span>
                                    </div>
                                </button>
                                <div className={styles.lendersActions}>
                                    <button
                                        type="button"
                                        className={styles.lendersCopyButton}
                                        onClick={copyLendersList}
                                        title="Copy lender names, one per line"
                                    >
                                        <FaCopy />
                                        {lendersCopied === 'list' ? 'Copied' : 'Copy lenders'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.lendersCopyButton}
                                        onClick={copyLendersCsv}
                                        title="Copy as CSV (lender,count)"
                                    >
                                        <FaCopy />
                                        {lendersCopied === 'csv' ? 'Copied' : 'Copy CSV'}
                                    </button>
                                </div>
                            </div>
                            {lendersOpen && (
                                <div className={styles.lendersTableWrapper}>
                                    <table className={styles.lendersTable}>
                                        <thead>
                                            <tr>
                                                <th>Lender name (raw)</th>
                                                <th className={styles.lendersCountCol}>Claims</th>
                                                <th className={styles.lendersFindCol}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {unapprovedLenders.map((lender) => (
                                                <tr key={lender.name}>
                                                    <td>{lender.name}</td>
                                                    <td className={styles.lendersCountCol}>
                                                        {formatNumber(lender.count)}
                                                    </td>
                                                    <td className={styles.lendersFindCol}>
                                                        <button
                                                            type="button"
                                                            className={styles.linkButton}
                                                            onClick={() => {
                                                                setActiveTab('ineligible')
                                                                setSearchIneligible(lender.name)
                                                            }}
                                                            title="Filter the ineligible list to claims with this lender"
                                                        >
                                                            Find claims
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    <div className={styles.tabs} role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'ineligible'}
                            className={`${styles.tab} ${activeTab === 'ineligible' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('ineligible')}
                        >
                            Ineligible for funding ({analysis.counts.ineligibleClients})
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'eligible'}
                            className={`${styles.tab} ${activeTab === 'eligible' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('eligible')}
                        >
                            Eligible ({analysis.counts.eligibleClients})
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'eligibleUnfunded'}
                            className={`${styles.tab} ${activeTab === 'eligibleUnfunded' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('eligibleUnfunded')}
                        >
                            Eligible unfunded ({eligibleUnfundedStats.clients})
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'notInUR'}
                            className={`${styles.tab} ${activeTab === 'notInUR' ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab('notInUR')}
                        >
                            Not in UR ({notInURStats.clients})
                        </button>
                    </div>

                    {activeTab === 'ineligible' ? (
                        <div className={styles.tabPanel}>
                            <div className={styles.toolbar}>
                                <div className={styles.searchWrapper}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder="Search by name, email, case ref, policy or lender…"
                                        value={searchIneligible}
                                        onChange={(e) => setSearchIneligible(e.target.value)}
                                    />
                                </div>
                                <div className={styles.toolbarActions}>
                                    <button
                                        type="button"
                                        className={styles.deleteButton}
                                        onClick={openDeletePreview}
                                        disabled={
                                            analysis.ineligibleClients.length === 0 || deletePreviewLoading
                                        }
                                        title="Permanently delete every unfundable client and all their linked data"
                                    >
                                        <FaTrashAlt className={styles.downloadButtonIcon} />
                                        {deletePreviewLoading ? 'Building preview…' : 'Delete unfundable clients'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.downloadButton}
                                        onClick={downloadIneligibleReport}
                                        disabled={analysis.ineligibleClients.length === 0}
                                        title="Download a CSV report of every ineligible client"
                                    >
                                        <FaDownload className={styles.downloadButtonIcon} />
                                        Download report
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.linkButton} ${
                                            groupIneligible ? styles.linkButtonActive : ''
                                        }`}
                                        onClick={() => setGroupIneligible((s) => !s)}
                                        title="Group ineligible clients by their rejection reason"
                                    >
                                        {groupIneligible ? 'Grouped by reason' : 'Group by reason'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => expandAll(filteredIneligible)}
                                    >
                                        Expand all
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => collapseAll(filteredIneligible)}
                                    >
                                        Collapse all
                                    </button>
                                </div>
                            </div>
                            {renderIneligibleAccordion(filteredIneligible)}
                        </div>
                    ) : activeTab === 'eligible' ? (
                        <div className={styles.tabPanel}>
                            <div className={styles.toolbar}>
                                <div className={styles.searchWrapper}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder="Search by name, email, case ref, policy or lender…"
                                        value={searchEligible}
                                        onChange={(e) => setSearchEligible(e.target.value)}
                                    />
                                </div>
                                <div className={styles.toolbarActions}>
                                    <button
                                        type="button"
                                        className={styles.downloadButton}
                                        onClick={() => downloadEligibleReport('all')}
                                        disabled={analysis.eligibleClients.length === 0}
                                        title="Download a CSV of every eligible client and their claims"
                                    >
                                        <FaDownload className={styles.downloadButtonIcon} />
                                        Download report
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => expandAll(filteredEligible)}
                                    >
                                        Expand all
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => collapseAll(filteredEligible)}
                                    >
                                        Collapse all
                                    </button>
                                </div>
                            </div>
                            {renderEligibleAccordion(
                                filteredEligible,
                                analysis.eligibleClients.length > 0
                                    ? 'No clients match your search.'
                                    : 'No eligible clients for this introducer.',
                                true
                            )}
                        </div>
                    ) : activeTab === 'eligibleUnfunded' ? (
                        <div className={styles.tabPanel}>
                            <div className={styles.unfundedBanner}>
                                <strong>{formatNumber(eligibleUnfundedStats.clients)}</strong>{' '}
                                client{eligibleUnfundedStats.clients === 1 ? '' : 's'} with{' '}
                                <strong>{formatNumber(eligibleUnfundedStats.claims)}</strong>{' '}
                                fundable claim{eligibleUnfundedStats.claims === 1 ? '' : 's'} worth{' '}
                                <strong>{formatMoney(eligibleUnfundedStats.value)}</strong>{' '}
                                {eligibleUnfundedStats.clients === 1 ? 'is' : 'are'} eligible for funding
                                but not yet added to a funding batch.
                            </div>
                            <div className={styles.toolbar}>
                                <div className={styles.searchWrapper}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder="Search by name, email, case ref, policy or lender…"
                                        value={searchEligibleUnfunded}
                                        onChange={(e) => setSearchEligibleUnfunded(e.target.value)}
                                    />
                                </div>
                                <div className={styles.toolbarActions}>
                                    <button
                                        type="button"
                                        className={styles.downloadButton}
                                        onClick={() => downloadEligibleReport('unfunded')}
                                        disabled={eligibleUnfunded.length === 0}
                                        title="Download a CSV report of eligible clients not yet in a funding batch"
                                    >
                                        <FaDownload className={styles.downloadButtonIcon} />
                                        Download report
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => expandAll(filteredEligibleUnfunded)}
                                    >
                                        Expand all
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => collapseAll(filteredEligibleUnfunded)}
                                    >
                                        Collapse all
                                    </button>
                                </div>
                            </div>
                            {renderEligibleAccordion(
                                filteredEligibleUnfunded,
                                eligibleUnfunded.length > 0
                                    ? 'No clients match your search.'
                                    : 'Every eligible client for this introducer is already in a funding batch.',
                                false
                            )}
                        </div>
                    ) : (
                        <div className={styles.tabPanel}>
                            <div className={styles.unfundedBanner}>
                                <strong>{formatNumber(notInURStats.clients)}</strong>{' '}
                                client{notInURStats.clients === 1 ? '' : 's'} with{' '}
                                <strong>{formatNumber(notInURStats.claims)}</strong>{' '}
                                claim{notInURStats.claims === 1 ? '' : 's'} worth{' '}
                                <strong>{formatMoney(notInURStats.value)}</strong>{' '}
                                {notInURStats.clients === 1 ? 'is' : 'are'} not currently in a UR —
                                these definitely did not get funded.
                            </div>
                            <div className={styles.toolbar}>
                                <div className={styles.searchWrapper}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        placeholder="Search by name, email, case ref, policy or lender…"
                                        value={searchNotInUR}
                                        onChange={(e) => setSearchNotInUR(e.target.value)}
                                    />
                                </div>
                                <div className={styles.toolbarActions}>
                                    <button
                                        type="button"
                                        className={styles.downloadButton}
                                        onClick={downloadNotInURReport}
                                        disabled={notInUR.length === 0}
                                        title="Download a CSV of every client and claim not currently in a UR"
                                    >
                                        <FaDownload className={styles.downloadButtonIcon} />
                                        Download report
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => expandAll(filteredNotInUR)}
                                    >
                                        Expand all
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.linkButton}
                                        onClick={() => collapseAll(filteredNotInUR)}
                                    >
                                        Collapse all
                                    </button>
                                </div>
                            </div>
                            {renderNotInURAccordion(filteredNotInUR)}
                        </div>
                    )}
                </div>
            )}

            {showDeleteModal && deletePreview && (
                <div className={styles.modalOverlay} onClick={closeDeleteModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <FaExclamationTriangle className={styles.modalWarningIcon} />
                            <h3>Delete unfundable clients — {displayIntroducerLabel(deletePreview.introducer)}</h3>
                        </div>

                        <div className={styles.modalWarningBanner}>
                            This permanently deletes{' '}
                            <strong>{formatNumber(deletePreview.clientCount)} clients</strong> and{' '}
                            <strong>{formatNumber(deletePreview.totalRows)} database rows</strong> from the{' '}
                            {displayIntroducerLabel(deletePreview.introducer)} schema. This cannot be undone.
                        </div>

                        <div className={styles.previewSection}>
                            <h4>Rows to be deleted</h4>
                            <table className={styles.previewTable}>
                                <tbody>
                                    {Object.entries(deletePreview.tableCounts).map(([table, count]) => (
                                        <tr key={table}>
                                            <td>{table}</td>
                                            <td className={styles.previewCount}>{formatNumber(count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {deletePreview.skippedTables?.length > 0 && (
                                <p className={styles.previewNote}>
                                    Left untouched: {deletePreview.skippedTables.join(', ')}
                                </p>
                            )}
                        </div>

                        <div className={styles.previewSection}>
                            <h4>By rejection reason</h4>
                            <table className={styles.previewTable}>
                                <tbody>
                                    {Object.entries(deletePreview.byReason)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([reason, count]) => (
                                            <tr key={reason}>
                                                <td>{labelClientReason(reason)}</td>
                                                <td className={styles.previewCount}>{formatNumber(count)}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {deletePreview.sample.length > 0 && (
                            <div className={styles.previewSection}>
                                <h4>Sample ({deletePreview.sample.length} of {formatNumber(deletePreview.clientCount)})</h4>
                                <table className={styles.previewTable}>
                                    <tbody>
                                        {deletePreview.sample.map((c) => (
                                            <tr key={String(c.introducerClientId)}>
                                                <td>
                                                    {c.name || '—'}{' '}
                                                    <span className={styles.previewMuted}>{c.email}</span>
                                                </td>
                                                <td className={styles.previewCount}>
                                                    {labelClientReason(c.reason)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className={styles.confirmSection}>
                            <label htmlFor="deleteConfirm">
                                Type <strong>delete</strong> to confirm
                            </label>
                            <input
                                id="deleteConfirm"
                                type="text"
                                className={styles.confirmInput}
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder="delete"
                                autoComplete="off"
                                disabled={isDeleting}
                            />
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={closeDeleteModal}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.confirmDeleteButton}
                                onClick={handleDeleteUnfundable}
                                disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeleting}
                            >
                                <FaTrashAlt />
                                {isDeleting
                                    ? 'Deleting…'
                                    : `Delete ${formatNumber(deletePreview.clientCount)} clients`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
